/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useVoiceGuidesList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Custom hook for managing Voice Guides list.
    Integrates with useDataGrid for pagination, sorting, and search.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useDataGrid, DataGridColumn } from '../../../hooks/useDataGrid';
import { fetchVoiceGuides } from '../api';
import { VoiceGuide } from '../types';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';

/**
 * Custom React hook for managing the PBX Voice Guides DataGrid list.
 * Integrates directly with `useDataGrid` for pagination, sorting, and search capabilities.
 * 
 * @param initialColumns - Optional array of DataGrid columns to initialize the list with.
 * @returns The DataGrid state and methods for managing the Voice Guides list.
 */
export function useVoiceGuidesList(initialColumns?: DataGridColumn<VoiceGuide>[]) {
  const { showError } = useNotifications();
  const { t } = useI18n();

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchVoiceGuides(params);
        return {
          data: result.data || [],
          pagination: result.pagination || {
            page: 1,
            limit: 25,
            total: 0,
            totalPages: 1,
          },
        };
      } catch (error) {
        showError(t('voiceGuides:notifications.loadFailed') || 'Unable to load voice guides');
        return {
          data: [] as VoiceGuide[],
          pagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
        };
      }
    },
    [showError, t],
  );

  return useDataGrid({
    fetchData,
    initialColumns,
    initialSortBy: 'language_code',
    initialSortOrder: 'asc',
    initialLimit: 25,
    moduleName: 'voiceGuides',
  });
}

